import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/IntegrationsSection.css';

const IntegrationsSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const integrations = [
    // AI & Machine Learning
    {
      id: 1,
      name: 'OpenAI',
      category: 'IA & ML',
      description: 'Potencia tus chatbots con GPT-4 y ChatGPT',
      hoverMessage: 'Integra la IA más avanzada del mundo para conversaciones naturales y respuestas inteligentes',
      logo: '/integrations/openAI.svg',
      color: '#00A67E'
    },
    {
      id: 2,
      name: 'Claude',
      category: 'IA & ML',
      description: 'IA avanzada de Anthropic para conversaciones naturales',
      hoverMessage: 'IA ética y segura para análisis complejos y respuestas contextuales precisas',
      logo: '/integrations/claude.svg',
      color: '#CC785C'
    },
    {
      id: 3,
      name: 'Gemini',
      category: 'IA & ML',
      description: 'IA multimodal de Google para análisis completo',
      hoverMessage: 'Procesa texto, imágenes y datos simultáneamente con la IA multimodal de Google',
      logo: '/integrations/google-gemini.svg',
      color: '#4285F4'
    },
    {
      id: 4,
      name: 'Dialogflow',
      category: 'IA & ML',
      description: 'Procesamiento de lenguaje natural de Google',
      hoverMessage: 'Comprende intenciones y entidades con el NLP más avanzado de Google',
      logo: '/integrations/dialogflow.svg',
      color: '#FF9800'
    },
    {
      id: 5,
      name: 'Google Speech',
      category: 'IA & ML',
      description: 'Reconocimiento y síntesis de voz en tiempo real',
      hoverMessage: 'Convierte voz a texto y texto a voz con precisión superior al 95%',
      logo: '/integrations/Google-Speech.svg',
      color: '#4285F4'
    },
    {
      id: 6,
      name: 'Wit.ai',
      category: 'IA & ML',
      description: 'Comprensión de lenguaje natural de Meta',
      hoverMessage: 'Entrena modelos de NLP personalizados para tu industria específica',
      logo: '/integrations/wit.ai_.svg',
      color: '#4267B2'
    },

    // Messaging & Social
    {
      id: 7,
      name: 'WhatsApp',
      category: 'Mensajería',
      description: 'API oficial de WhatsApp Business para automatización',
      hoverMessage: 'Conecta con más de 2 billones de usuarios con la API oficial de Meta',
      logo: '/integrations/whatsapp.svg',
      color: '#25D366'
    },
    {
      id: 8,
      name: 'Facebook',
      category: 'Mensajería',
      description: 'Integración completa con Facebook Messenger',
      hoverMessage: 'Automatiza respuestas en la red social más grande del mundo',
      logo: '/integrations/Facebook.svg',
      color: '#1877F2'
    },
    {
      id: 9,
      name: 'Messenger',
      category: 'Mensajería',
      description: 'Automatiza conversaciones en Facebook Messenger',
      hoverMessage: 'Chatbots inteligentes para 1.3 billones de usuarios de Messenger',
      logo: '/integrations/messenger.svg',
      color: '#0084FF'
    },
    {
      id: 10,
      name: 'Instagram',
      category: 'Mensajería',
      description: 'Gestiona mensajes directos e interacciones',
      hoverMessage: 'Automatiza DMs y comentarios en la plataforma visual más popular',
      logo: '/integrations/instagram.svg',
      color: '#E4405F'
    },
    {
      id: 11,
      name: 'Telegram',
      category: 'Mensajería',
      description: 'Bots inteligentes para canales y grupos de Telegram',
      hoverMessage: 'Crea bots poderosos con la API más flexible de mensajería',
      logo: '/integrations/telegram.svg',
      color: '#0088CC'
    },
    {
      id: 12,
      name: 'Viber',
      category: 'Mensajería',
      description: 'Automatización de mensajes en Viber Business',
      hoverMessage: 'Llega a usuarios en Europa del Este y Asia con Viber Business',
      logo: '/integrations/viber.svg',
      color: '#665CAC'
    },

    // Communication & CRM
    {
      id: 13,
      name: 'Twilio',
      category: 'Comunicación',
      description: 'SMS, llamadas y WhatsApp a través de Twilio',
      hoverMessage: 'Plataforma global de comunicaciones con cobertura en 180+ países',
      logo: '/integrations/Twilio.svg',
      color: '#F22F46'
    },
    {
      id: 14,
      name: 'ClickSend',
      category: 'Comunicación',
      description: 'Envío masivo de SMS y notificaciones',
      hoverMessage: 'Envía SMS masivos con entregabilidad del 99.9% a nivel mundial',
      logo: '/integrations/clicksend.svg',
      color: '#FF6B35'
    },
    {
      id: 15,
      name: 'LeadSquared',
      category: 'CRM',
      description: 'Gestión avanzada de leads y automatización',
      hoverMessage: 'Sincroniza leads automáticamente y gestiona el embudo completo de ventas',
      logo: '/integrations/LeadSquared.svg',
      color: '#FF6B35'
    },

    // Productivity & Calendars
    {
      id: 16,
      name: 'Google Sheets',
      category: 'Productividad',
      description: 'Sincroniza datos con hojas de cálculo automáticamente',
      hoverMessage: 'Conecta tus chatbots con Google Sheets para análisis y reportes en tiempo real',
      logo: '/integrations/google-sheets.svg',
      color: '#34A853'
    },
    {
      id: 17,
      name: 'Google Calendar',
      category: 'Productividad',
      description: 'Programa citas y reuniones desde el chat',
      hoverMessage: 'Automatiza la programación de citas y sincroniza con Google Calendar',
      logo: '/integrations/google-calendar.svg',
      color: '#4285F4'
    },
    {
      id: 18,
      name: 'Microsoft Calendar',
      category: 'Productividad',
      description: 'Integración con Outlook y Microsoft 365',
      hoverMessage: 'Programa reuniones y eventos directamente en Outlook y Microsoft 365',
      logo: '/integrations/ms-calendar.svg',
      color: '#0078D4'
    },

    // E-commerce & Payments
    {
      id: 19,
      name: 'WooCommerce',
      category: 'E-commerce',
      description: 'Automatiza tu tienda de WordPress',
      hoverMessage: 'Conecta tu tienda WooCommerce para seguimiento de pedidos y soporte automatizado',
      logo: '/integrations/woocommerce.svg',
      color: '#96588A'
    },
    {
      id: 20,
      name: 'Shopify',
      category: 'E-commerce',
      description: 'Integración completa con Shopify',
      hoverMessage: 'Automatiza ventas, inventario y atención al cliente en Shopify',
      logo: '/integrations/shopify.svg',
      color: '#7AB55C'
    },
    {
      id: 21,
      name: 'PayPal',
      category: 'Pagos',
      description: 'Procesa pagos seguros en la conversación',
      hoverMessage: 'Acepta pagos directamente en el chat con la seguridad de PayPal',
      logo: '/integrations/PayPal.svg',
      color: '#00457C'
    },
    {
      id: 22,
      name: 'Stripe',
      category: 'Pagos',
      description: 'Pagos online rápidos y seguros',
      hoverMessage: 'Procesa pagos con tarjeta directamente en la conversación con Stripe',
      logo: '/integrations/stripe.svg',
      color: '#635BFF'
    },
    {
      id: 23,
      name: 'Razorpay',
      category: 'Pagos',
      description: 'Solución de pagos completa para India',
      hoverMessage: 'Acepta pagos en India con todos los métodos locales populares',
      logo: '/integrations/razorpay.svg',
      color: '#3395FF'
    },

    // Email Marketing
    {
      id: 24,
      name: 'Mailchimp',
      category: 'Email Marketing',
      description: 'Automatiza campañas de email marketing',
      hoverMessage: 'Sincroniza contactos y automatiza campañas de email con Mailchimp',
      logo: '/integrations/mailchimp.svg',
      color: '#FFE01B'
    },
    {
      id: 25,
      name: 'SendGrid',
      category: 'Email Marketing',
      description: 'Envío masivo de emails transaccionales',
      hoverMessage: 'Envía emails transaccionales con entregabilidad superior al 99%',
      logo: '/integrations/sendgrid.svg',
      color: '#1A82E2'
    },
    {
      id: 26,
      name: 'Drip',
      category: 'Email Marketing',
      description: 'Marketing automation para e-commerce',
      hoverMessage: 'Automatización avanzada de email marketing para tiendas online',
      logo: '/integrations/drip.svg',
      color: '#EA4C89'
    },
    {
      id: 27,
      name: 'SendFox',
      category: 'Email Marketing',
      description: 'Email marketing simple y efectivo',
      hoverMessage: 'Plataforma de email marketing fácil de usar para emprendedores',
      logo: '/integrations/sendfox.svg',
      color: '#FF6B35'
    },
    {
      id: 28,
      name: 'SMTP',
      category: 'Email Marketing',
      description: 'Conecta cualquier servidor de email SMTP',
      hoverMessage: 'Conecta cualquier proveedor de email usando configuración SMTP estándar',
      logo: '/integrations/envelope.svg',
      color: '#34495E'
    },

    // Automation & Development
    {
      id: 29,
      name: 'Zapier',
      category: 'Automatización',
      description: 'Conecta con más de 5000 aplicaciones',
      hoverMessage: 'Automatiza workflows conectando con más de 5000 aplicaciones diferentes',
      logo: '/integrations/zapier.svg',
      color: '#FF4A00'
    },
    {
      id: 30,
      name: 'Make',
      category: 'Automatización',
      description: 'Automatización visual avanzada (ex-Integromat)',
      hoverMessage: 'Crea automatizaciones complejas con interfaz visual drag & drop',
      logo: '/integrations/integromate.svg',
      color: '#6366F1'
    },
    {
      id: 31,
      name: 'PlatformLy',
      category: 'Automatización',
      description: 'Plataforma todo-en-uno para marketing digital',
      hoverMessage: 'Suite completa de herramientas de marketing digital y automatización',
      logo: '/integrations/platformLy.svg',
      color: '#8B5CF6'
    },
    {
      id: 32,
      name: 'Webhooks',
      category: 'Desarrollo',
      description: 'Integra cualquier sistema con webhooks personalizados',
      hoverMessage: 'Conecta cualquier sistema que soporte webhooks para integraciones ilimitadas',
      logo: '/integrations/webhooks.svg',
      color: '#f9a550'
    }
  ];

  const categories = [...new Set(integrations.map(integration => integration.category))];

  return (
    <section id="integrations" className="integrations-section section section-page" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2>Integraciones <span className="text-gradient">Sin Límites</span></h2>
          <p>
            Conecta Runamatic con todas las herramientas que ya usas en tu negocio. 
            Nuestra plataforma se integra fácilmente con los sistemas más populares.
          </p>
        </motion.div>

        <motion.div 
          className="categories-filter"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {categories.map((category, index) => (
            <motion.div
              key={category}
              className="category-tag"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              {category}
            </motion.div>
          ))}
        </motion.div>

        {/* Featured MCP Integration */}
        <motion.div
          className="mcp-featured-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.div
            className="mcp-featured-card"
            whileHover={{ y: -5, scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <div className="mcp-card-content">
              <div className="mcp-left-section">
                <motion.div 
                  className="mcp-logo"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <img 
                    src="/integrations/mcp.png" 
                    alt="MCP Servers logo"
                    className="mcp-icon"
                  />
                </motion.div>
                <div className="mcp-info">
                  <h3 className="mcp-title">MCP Servers</h3>
                  <span className="mcp-category">Model Context Protocol</span>
                  <p className="mcp-description">
                    Conecta Runamatic con cualquier MCP server. Un protocolo estándar que permite 
                    a las aplicaciones de IA comunicarse de forma segura con herramientas externas, 
                    bases de datos y servicios web de manera consistente.
                  </p>
                </div>
              </div>
              
              <div className="mcp-right-section">
                <div className="mcp-features">
                  <div className="mcp-feature">
                    <div className="feature-icon">🔧</div>
                    <div className="feature-text">
                      <h4>Herramientas Personalizadas</h4>
                      <p>Conecta APIs y servicios específicos</p>
                    </div>
                  </div>
                  <div className="mcp-feature">
                    <div className="feature-icon">🗄️</div>
                    <div className="feature-text">
                      <h4>Recursos Dinámicos</h4>
                      <p>Acceso a archivos y datos externos</p>
                    </div>
                  </div>
                  <div className="mcp-feature">
                    <div className="feature-icon">💬</div>
                    <div className="feature-text">
                      <h4>Prompts Inteligentes</h4>
                      <p>Plantillas reutilizables y contextuales</p>
                    </div>
                  </div>
                </div>
                
                <div className="mcp-status">
                  <span className="status-indicator"></span>
                  <span className="status-text">Disponible</span>
                </div>
              </div>
            </div>

            <div className="mcp-overlay">
              <div className="mcp-overlay-content">
                <h4 className="mcp-overlay-title">MCP Servers</h4>
                <p className="mcp-overlay-description">
                  El Protocolo de Comunicación Modelo (MCP) es un estándar abierto que facilita 
                  integraciones seguras y escalables entre aplicaciones de IA y servicios externos. 
                  Con MCP, Runamatic puede conectarse a cualquier servidor compatible, 
                  expandiendo infinitamente las capacidades de tu chatbot.
                </p>
                <div className="mcp-overlay-features">
                  <span className="overlay-tag">Protocolo Abierto</span>
                  <span className="overlay-tag">Extensible</span>
                  <span className="overlay-tag">Seguro</span>
                  <span className="overlay-tag">Escalable</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <div className="integrations-grid">
          {integrations.map((integration, index) => (
            <motion.div
              key={integration.id}
              className="integration-card"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.05 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="integration-header">
                <motion.div 
                  className="integration-logo"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <img 
                    src={integration.logo} 
                    alt={`${integration.name} logo`}
                    className="integration-icon"
                  />
                </motion.div>
                <div className="integration-info">
                  <h4 className="integration-name">{integration.name}</h4>
                  <span className="integration-category">{integration.category}</span>
                </div>
              </div>
              
              <p className="integration-description">{integration.description}</p>
              
              <div className="integration-status">
                <span className="status-indicator"></span>
                <span className="status-text">Disponible</span>
              </div>

              <div className="integration-overlay">
                <div className="overlay-content">
                  <h4 className="overlay-title">{integration.name}</h4>
                  <p className="overlay-description">{integration.hoverMessage}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="integrations-bottom"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="bottom-content">
            <div className="bottom-left">
              <h3>¿No encuentras la integración que necesitas?</h3>
              <p>
                Nuestro equipo de desarrollo puede crear integraciones personalizadas 
                para conectar Runamatic con cualquier sistema que uses en tu empresa.
              </p>
            </div>
            
            <div className="bottom-right">
              <motion.button
                className="btn btn-white"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const email = 'hola@runamatic.io';
                  const message = `Para consultas sobre integraciones personalizadas, escríbenos a: ${email}`;
                  
                  // Intentar copiar al portapapeles
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(email).then(() => {
                      alert(`${message}\n\n✅ Email copiado al portapapeles`);
                    }).catch(() => {
                      alert(message);
                    });
                  } else {
                    alert(message);
                  }
                }}
              >
                Consultanos por una integración
              </motion.button>
              
              <motion.a
                href="/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-white"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Ver Documentación
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default IntegrationsSection; 